import { Component, EventListener, startComponents, stopComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { Tooltip, ITooltipFetchService } from '../../../js/tooltip';
import { TooltipFactory } from '../../../js/tooltip-factory';
import createFocusTrap, { FocusTrap } from 'focus-trap';
import Popper from 'popper.js';

const enum Selectors {
	TooltipPopupContent = '.js-m-tooltip-popup__content',
	TooltipPopupCloseButton = '.js-m-tooltip-popup__close-button',
}

const enum States {
	TooltipPopupVisible = 'state-m-tooltip-popup--visible',
	TooltipPopupFetching = 'state-m-tooltip-popup--fetching',
	TooltipPopupError = 'state-m-tooltip-popup--error',
}

const enum Placement {
	top = 'top',
	right = 'right',
	bottom = 'bottom',
	left = 'left',
	// https://github.com/FezVrasta/popper.js/blob/master/docs/_includes/popper-documentation.md#Popper.placements
}

@Component('TooltipPopup')
export class TooltipPopup extends GondelJqueryComponent {
	public static createTooltipPopup(error: string): Promise<TooltipPopup> {
		return new Promise((resolve, reject) => {
			const $tooltipPopup = $(`
				<div data-g-name="TooltipPopup" class="m-tooltip-popup" tabindex="-1">
					<div class="m-tooltip-popup__arrow" x-arrow=""></div>
					<div class="m-tooltip-popup__close-button js-m-tooltip-popup__close-button">
						<div class="m-tooltip-popup__close-icon">
							<svg class="a-svg a-svg--close-button-16" role="presentation" focusable="false" aria-hidden="true">
								<use xlink:href="/etc.clientlibs/slevo/clientlibs/site/resources/sprites.svg#close-button-16"></use>
							</svg>
						</div>
					</div>
					<div class="m-tooltip-popup__content js-m-tooltip-popup__content">
						<div class="m-tooltip-popup__async-loader">
							<div class="a-loader js-a-loader a-loader--inline-block a-loader--background
								state-a-loader--visible state-a-loader--no-delay js-a-loader">
								<div class="a-loader__wrapper preloader-wrapper active">
									<div class="spinner-layer">
										<div class="circle-clipper left">
											<div class="circle"></div>
										</div>
										<div class="gap-patch">
											<div class="circle"></div>
										</div>
										<div class="circle-clipper right">
											<div class="circle"></div>
										</div>
									</div>
								</div>
							</div>
						</div>
						${
							error
								? `
							<div class="m-tooltip-popup__async-error">
								<div class="m-toast m-toast--inline">
									<div class="m-toast__icon">
										<svg class="a-svg a-svg--notification-info-32" role="presentation" focusable="false" aria-hidden="true">
											<use xlink:href="/etc.clientlibs/slevo/clientlibs/site/resources/sprites.svg#notification-info-32"></use>
										</svg>
									</div>
									<div class="m-toast__text">
										<p>${error}</p>
									</div>
								</div>
							</div>
						`
								: ''
						}
					</div>
				</div>
			`);

			startComponents($tooltipPopup)
				.then((startedComponents) => {
					const tooltipPopup: TooltipPopup = startedComponents[0] as TooltipPopup;
					resolve(tooltipPopup);
				})
				.catch((err) => {
					reject(err);
				});
		});
	}

	private visible: boolean = false;
	private focusTrap: FocusTrap;
	private popper: Popper;
	private tooltip: Tooltip;

	/**
	 * Event for closing tooltip when clicking on close
	 * @param ev {Event}	click event
	 */
	@EventListener('click', Selectors.TooltipPopupCloseButton)
	public _onHandleCloseClick(ev: MouseEvent) {
		ev.preventDefault();
		ev.stopPropagation();
		if (this.focusTrap) {
			this.focusTrap.deactivate();
		}
	}

	public bindTooltip(tooltip: Tooltip): TooltipPopup | undefined {
		if (tooltip) {
			this.tooltip = tooltip;
			this.$ctx.appendTo(document.body);
			this.popper = this.createPopper(tooltip.$ctx);
			this.focusTrap = this.createFocusTrap(tooltip.$ctx);
			return this;
		}
		return undefined;
	}

	public get isVisible(): boolean {
		return this.visible;
	}

	public toogleVisible(visible: boolean = true) {
		if (this.popper && this.focusTrap) {
			if (visible) {
				this.focusTrap.activate();
				this.popper.enableEventListeners();
				this.popper.scheduleUpdate();
				this.visible = true;
				this.$ctx.addClass(States.TooltipPopupVisible);
				this.getAsyncContent();
			} else {
				this.focusTrap.deactivate();
			}
		}
	}

	public getAsyncContent() {
		if (this.tooltip.fetchContent && this.tooltip.fetchService && this.tooltip.id) {
			this.fetchAsyncContent(this.tooltip.fetchService, this.tooltip.id);
		}
	}

	private createFocusTrap($reference: JQuery<HTMLElement> = this.$ctx) {
		return createFocusTrap(this.$ctx.get(0), {
			clickOutsideDeactivates: true,
			escapeDeactivates: true,
			initialFocus: $reference.get(0),
			onDeactivate: () => {
				this.$ctx.removeClass(States.TooltipPopupVisible);
				this.popper.disableEventListeners();
				setTimeout(() => {
					// failsafe so that clicking on the tooltip does not
					// show the popup again
					this.visible = false;
				}, 300);
			},
		});
	}

	private createPopper($reference: JQuery<HTMLElement> = this.$ctx) {
		return new Popper($reference.get(0), this.$ctx.get(0), {
			placement: this.getTooltipPlacement(),
			positionFixed: true,
			eventsEnabled: false,
			modifiers: {
				preventOverflow: {
					boundariesElement: 'scrollParent',
				},
			},
		});
	}

	private getTooltipPlacement(): Placement {
		const placement = this.$ctx.data('placement');
		if (placement) {
			switch (placement) {
				case Placement.top:
					return Placement.top;
				case Placement.right:
					return Placement.right;
				case Placement.bottom:
					return Placement.bottom;
				case Placement.left:
					return Placement.left;
			}
		}
		return Placement.top;
	}

	private async fetchAsyncContent(service: ITooltipFetchService, tooltipId: string) {
		const $content = this.$ctx.find(Selectors.TooltipPopupContent);
		if ($content) {
			this.$ctx.removeClass(States.TooltipPopupError);
			this.$ctx.addClass(States.TooltipPopupFetching);
			try {
				const fragment = await TooltipFactory.getContentFragment(service, tooltipId);
				stopComponents($content);
				$content.empty(); // remove loader + error message
				$(fragment)
					.clone()
					.appendTo($content);
				startComponents($content);
				this.tooltip.fetchContent = false;
			} catch (error) {
				this.$ctx.addClass(States.TooltipPopupError);
			} finally {
				this.$ctx.removeClass(States.TooltipPopupFetching);
				this.popper.scheduleUpdate();
			}
		}
	}
}
